import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Een aanvraag van een club voor een tegenstander die nergens staat (auth: /ClubRequest). */
export interface ClubRequest {
  id: number;
  name: string;
  place?: string;
  countryCode?: string;
  note?: string;
  requestedByClub: string;
  requestedByClubName?: string;
  requestedByUser?: string;
  matchSids: string[];
  status: 'pending' | 'approved' | 'rejected' | string;
  resolvedClubSid?: string;
  resolvedName?: string;
  rejectReason?: string;
  createdAtUtc: string;
  resolvedAtUtc?: string;
}

/** Treffer uit GET /ClubCatalog/Search (bestaande club, catalogus of API-Football). */
export interface CatalogHit {
  kind: 'club' | 'catalog' | string;
  source?: string;
  catalogKey?: string;
  clubSid?: string;
  name: string;
  place?: string;
  countryCode?: string;
  alreadyInDb?: boolean;
  matchScore?: number;
}

export interface ApproveBody {
  clubSid?: string;
  source?: string;
  catalogKey?: string;
  name?: string;
  place?: string;
  countryCode?: string;
}

export interface ApproveResult {
  approved: boolean;
  needsConfirmation?: boolean;
  candidates?: CatalogHit[];
  request: ClubRequest;
  clubSid?: string;
  name?: string;
  requestedByClub?: string;
  matchSids?: string[];
}

export interface FillResult {
  albumSid: string;
  albumCreated: boolean;
  total: number;
  filled: number;
  /** Had al een uitclub (bijv. met de hand ingevuld). */
  skipped: number;
  failed: number;
}

interface AlbumRij {
  sid: string;
  name: string;
}

interface MatchKant {
  clubAlbum?: string;
  name?: string;
}

interface MatchRij {
  sid: string;
  title?: string;
  away?: MatchKant;
}

/** Namen vergelijken zoals een mens dat doet: hoofdletters en dubbele spaties tellen niet. */
export function zelfdeNaam(a: string | undefined, b: string | undefined): boolean {
  const n = (s: string | undefined) => (s ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  return !!n(a) && n(a) === n(b);
}

/**
 * Wat er bij een wedstrijd moet veranderen, of null als hij al een uitclub
 * heeft. Een titel die nog op "?" eindigt ("Thuis vs ?") krijgt de naam;
 * een eigen titel blijft staan.
 */
export function wedstrijdAanvulling(match: MatchRij, albumSid: string, naam: string): { away: { clubAlbum: string }; title?: string } | null {
  if ((match.away?.clubAlbum ?? '').trim()) {
    return null;
  }
  const body: { away: { clubAlbum: string }; title?: string } = { away: { clubAlbum: albumSid } };
  const titel = (match.title ?? '').trim();
  if (titel.endsWith(' vs ?')) {
    body.title = titel.slice(0, -1) + naam;
  }
  return body;
}

/** Toastregel na het invullen van de uitclub. */
export function fillMelding(naam: string, r: FillResult): string {
  if (!r.total) {
    return `${naam} approved. No matches were waiting for it.`;
  }
  const delen = [`${naam} approved. Filled ${r.filled} of ${r.total} ${r.total === 1 ? 'match' : 'matches'}`];
  if (r.skipped) delen.push(`${r.skipped} already had an opponent`);
  if (r.failed) delen.push(`${r.failed} failed`);
  return delen.join('; ') + '.';
}

export function foutTekst(e: unknown): string {
  const err = e as HttpErrorResponse;
  return err?.error?.Message || err?.error?.message || err?.message || 'Something went wrong';
}

@Injectable({ providedIn: 'root' })
export class ClubRequestsService {
  private readonly authUrl = `${environment.authUrl}/ClubRequest`;

  /** Openstaande aanvragen, voor de teller in het menu. */
  readonly pendingCount = signal(0);

  constructor(private http: HttpClient) {}

  public list(status: string, limit = 100, page = 0): Observable<{ items: ClubRequest[]; total: number }> {
    const params = new HttpParams().set('status', status).set('limit', String(limit)).set('page', String(page));
    return this.http.get<{ items?: ClubRequest[]; total?: number }>(this.authUrl, { params })
      .pipe(map(res => ({ items: res?.items ?? [], total: res?.total ?? 0 })));
  }

  public refreshCount(): void {
    const params = new HttpParams().set('status', 'pending');
    this.http.get<{ count: number }>(`${this.authUrl}/Count`, { params }).subscribe({
      next: res => this.pendingCount.set(res?.count ?? 0),
      error: () => undefined,
    });
  }

  public approve(id: number, body: ApproveBody): Observable<ApproveResult> {
    return this.http.post<ApproveResult>(`${this.authUrl}/${id}/Approve`, body);
  }

  public reject(id: number, reason: string): Observable<ClubRequest> {
    return this.http.post<ClubRequest>(`${this.authUrl}/${id}/Reject`, { reason });
  }

  public searchCatalog(q: string): Observable<CatalogHit[]> {
    const params = new HttpParams().set('q', q).set('limit', '20');
    return this.http
      .get<{ clubs?: CatalogHit[]; catalog?: CatalogHit[]; apiClubs?: CatalogHit[] }>(`${environment.authUrl}/ClubCatalog/Search`, { params })
      .pipe(map(res => {
        // Zelfde volgorde als de webapp: bestaande clubs, catalogus, API.
        const seen = new Set<string>();
        const out: CatalogHit[] = [];
        for (const h of [...(res?.clubs ?? []), ...(res?.catalog ?? []), ...(res?.apiClubs ?? [])]) {
          const key = h.clubSid ? `c:${h.clubSid}` : `${h.source ?? 'x'}:${h.catalogKey ?? h.name}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(h);
        }
        return out;
      }));
  }

  /**
   * Na goedkeuren, in arch als super-admin: het album met deze naam in de
   * aanvragende club (bestaand of nieuw), en daarmee de uitclub invullen op
   * de gekoppelde wedstrijden die er nog geen hebben.
   */
  public async fillMatches(requestedByClub: string, naam: string, matchSids: string[]): Promise<FillResult> {
    const clubParams = new HttpParams().set('club', requestedByClub);
    const lijst = await firstValueFrom(this.http.get<{ result?: { items?: AlbumRij[] } }>(
      `${environment.apiUrl}/ClubAlbum`,
      { params: clubParams.set('filter', naam).set('limit', '500') },
    ));
    let album = (lijst?.result?.items ?? []).find(a => zelfdeNaam(a.name, naam));
    const albumCreated = !album;
    if (!album) {
      album = await firstValueFrom(this.http.post<AlbumRij>(`${environment.apiUrl}/ClubAlbum`, { name: naam }, { params: clubParams }));
    }

    const uit: FillResult = { albumSid: album.sid, albumCreated, total: matchSids.length, filled: 0, skipped: 0, failed: 0 };
    for (const sid of matchSids) {
      try {
        const match = await firstValueFrom(this.http.get<MatchRij>(`${environment.apiUrl}/Match/${sid}`));
        const body = wedstrijdAanvulling(match, album.sid, naam);
        if (!body) {
          uit.skipped++;
          continue;
        }
        await firstValueFrom(this.http.patch(`${environment.apiUrl}/Match/${sid}`, body));
        uit.filled++;
      } catch {
        uit.failed++;
      }
    }
    return uit;
  }
}
